import React,
  {
    useCallback,
    useEffect,
    useMemo,
    useState,
  } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, ChevronRight, AlertTriangle, Eye, Lock, Send } from "lucide-react";
import { isManagedEvidenceReference, normalizeEvidenceLink } from "@/lib/evidenceRules";
import {
  evaluateAssessmentCompleteness,
  categoryBlockingIds,
} from "@/lib/assessmentCompleteness";

import { useToast } from "@/hooks/use-toast";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/Auth";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { useDisplayName } from "@/hooks/useDisplayName";

import { DomainSection } from "@/components/wizard/DomainSection";
import { usePortfolioServiceModels } from "@/hooks/useAssessmentServiceModels";
import { useQuestionOrdinals } from "@/hooks/useQuestionOrdinals";
import {
  resolveDisplayOrdinal,
  questionLabel,
  buildFallbackOrdinalMap,
  mergeOrdinalMaps,
} from "@/lib/questionOrdinals";

import { evidenceExamplesForServiceModels } from "@/configs/telecom/serviceModelEvidenceExamples";
import { AssessmentAssigneesPanel } from "@/components/assessment/AssessmentAssigneesPanel";
import { VendorSelectionSection } from "@/components/wizard/VendorSelectionSection";
import { SlideSwitch } from "@/components/animations/SlideSwitch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  fetchAssessmentQuestions,
  type AssessmentQuestionRow,
} from "@/api/fetchAssessmentQuestions";
import { fetchOrgName } from "@/api/fetch/fetchOrgName";
import { domainTheme, type DomainCode } from "@/lib/domainTheme";
import { DOMAIN_ORDER, DOMAIN_LABELS, HRV_DOMAIN_ORDER, HRV_DOMAIN_LABELS, ALL_DOMAIN_LABELS, TRIAGE_DOMAIN_ORDER } from "@/lib/functions";
import { NIST_CSF_DOMAIN_DESCRIPTIONS } from "@/lib/frameworkDescriptions/nistCsfDomains";
import { isHrvQuestionnaire, isSelfAssessmentOnly, isTelecomStyleQuestionnaire, requiresAllQuestionsAnswered } from "@/lib/questionnaireType";

// Contextual helper text for the Pre-Investment Triage sections — explains why each section matters.
const TRIAGE_SECTION_HELP: Record<string, string> = {
  PROFILE: "Capture the company's primary market, scale, regulatory footprint, and revenue model. These answers frame how the rest of the triage is interpreted.",
  M1: "Assess how visible and prominent the operator is in its market. Higher visibility typically attracts greater threat and political attention.",
  M2: "Capture the demand and complexity of the regulatory environment(s) the operator works under.",
  M3: "Describe the inherent attack surface of the network and infrastructure footprint.",
  M4: "Assess the sensitivity, volume, and cross-border movement of subscriber and operational data.",
  M5: "Evaluate vendor concentration, high-risk vendor exposure, and dependence on managed services.",
  M6: "Capture how complex day-to-day operations and change activity are.",
  M7: "Assess the breadth of privileged and third-party administrative access — typically the highest-impact compromise pathway.",
  M8: "Evaluate how dependent revenue is on digital channels and how impactful service disruption would be.",
  M9: "Capture external risk signals — public disclosures, geopolitical exposure, and reputation events.",
  M10: "Assess how complex it would be to recover critical services after a major disruption.",
  RED_FLAG: "Capture material red-flag conditions. These can independently elevate the recommendation, regardless of other answers.",
};

// --- NIST CSF functions/domains we display ---
const DOMAINS = DOMAIN_ORDER;
const FUNCTION_NAMES = DOMAIN_LABELS;

type DomainKey = DomainCode | string;

type AssessmentData = {
  id: string;
  org_id: string;
  org_name: string;
  questionnaire_id: string;
  status: string;
  submitted_at: string | null;
};

export default function Wizard() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { role: effectiveRole } = useEffectiveRole();
  const displayName = useDisplayName();
  const { models: portfolioServiceModels } = usePortfolioServiceModels(assessmentId);
  // Phase 2A closeout — frozen per-assessment question numbers (never index-derived).
  const { data: questionOrdinals } = useQuestionOrdinals(assessmentId);
  
  // Check if admin came from the Admin Console
  const cameFromAdminConsole = (location.state as any)?.fromAdminConsole === true;
  
  // Tier 1 PM acting as respondent: accessed via /wizard/:id route
  const isPMActingAsRespondent = effectiveRole === "portfolio_manager" && location.pathname.startsWith("/wizard/");
  // Portfolio Manager and Client Admin get read-only view (unless PM is acting as respondent)
  const isReadOnlyRole = (effectiveRole === "portfolio_manager" && !isPMActingAsRespondent) || effectiveRole === "client_admin";
  const isClientAdmin = effectiveRole === "client_admin";
  const isAdmin = effectiveRole === "admin";
  const isPM = isReadOnlyRole; // Read-only PM/client_admin (excludes PM acting as respondent)
  // Only admin can manage assignees (PM and client_admin are read-only)
  const canManageAssignees = effectiveRole === "admin";
  const { toast } = useToast();

  // --- core state ---
  const [assessment, setAssessment] = useState<AssessmentData | null>(null);
  const [rows, setRows] = useState<AssessmentQuestionRow[]>([]);
  const [responsesById, setResponsesById] = useState<
    Map<number, { score: number | null; comment: string | null; evidence_links?: string[] }>
  >(new Map());
  
  // --- reviewer ratings count (for PM status summary) ---
  const [reviewerRatingsCount, setReviewerRatingsCount] = useState<number>(0);
  
  // --- HRV vendor count for validation ---
  const [hrvVendorCount, setHrvVendorCount] = useState<number>(0);


  // Respondent lock: assessment is locked after submission
  const LOCKED_STATUSES = ["Submitted", "In Review", "Closed"];
  const isRespondentLocked = !isReadOnlyRole && !isAdmin && LOCKED_STATUSES.includes(assessment?.status ?? "");
  const isEffectivelyReadOnly = isPM || isRespondentLocked;

  // Confirmation checkbox state for self-assessment submission
  const [confirmChecked, setConfirmChecked] = useState(false);

  // --- UI state ---
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [activeDomain, setActiveDomain] = useState<string>(DOMAINS[0]);
  
  // --- evidence validation state ---
  const [evidenceErrors, setEvidenceErrors] = useState<Set<number>>(new Set());
  const [evidenceErrorMessage, setEvidenceErrorMessage] = useState<string | null>(null);
  const [aiStatus, setAiStatus] = useState<string | null>(null);

  // Detect domains dynamically from loaded questions — only include domains with questions
  const effectiveDomains = useMemo(() => {
    if (rows.length === 0) return DOMAINS as string[];
    const seen = new Set<string>();
    for (const r of rows) { if (r.domain) seen.add(r.domain); }
    const nistSet = new Set(DOMAINS as string[]);
    const allNist = [...seen].every(d => nistSet.has(d));
    if (allNist) {
      return (DOMAINS as string[]).filter(d => seen.has(d));
    }
    const hrvSet = new Set(HRV_DOMAIN_ORDER as string[]);
    const allHrv = [...seen].every(d => hrvSet.has(d));
    if (allHrv) return (HRV_DOMAIN_ORDER as string[]).filter(d => seen.has(d));
    const triageSet = new Set(TRIAGE_DOMAIN_ORDER as string[]);
    const allTriage = [...seen].every(d => triageSet.has(d));
    if (allTriage) return (TRIAGE_DOMAIN_ORDER as string[]).filter(d => seen.has(d));
    return [...seen];
  }, [rows]);

  const effectiveFunctionNames = useMemo(() => {
    return ALL_DOMAIN_LABELS;
  }, []);

  // Triage questionnaire detection — hide internal codes (M1, M2, RED_FLAG, PROFILE)
  // and show only full business-friendly labels in the sidebar/header.
  const isTriageQuestionnaire = useMemo(() => {
    const id = assessment?.questionnaire_id ?? "";
    return id === "telecom_pre_investment_triage_v1";
  }, [assessment?.questionnaire_id]);

  // --- helpers ---
  const handlePrevDomain = useCallback(() => {
    const currentIndex = effectiveDomains.indexOf(activeDomain);
    if (currentIndex > 0) {
      setActiveDomain(effectiveDomains[currentIndex - 1]);
    }
  }, [activeDomain, effectiveDomains]);

  // Reset active domain when effective domains change
  useEffect(() => {
    if (effectiveDomains.length > 0 && !effectiveDomains.includes(activeDomain)) {
      setActiveDomain(effectiveDomains[0]);
    }
  }, [effectiveDomains, activeDomain]);

  // --- derived: group rows by domain ---
  const rowsByDomain = useMemo(() => {
    const out: Record<string, AssessmentQuestionRow[]> = {};
    for (const d of effectiveDomains) { out[d] = []; }
    
    for (const r of rows) {
      if (out[r.domain]) {
        out[r.domain].push(r);
      }
    }
    return out;
  }, [rows, effectiveDomains]);

  // Rows in the exact order the respondent sees them (category order, then
  // in-category order). Used to derive the fallback numbering.
  const orderedRows = useMemo(
    () => effectiveDomains.flatMap((d) => rowsByDomain[d] ?? []),
    [effectiveDomains, rowsByDomain],
  );

  /**
   * Single numbering source for BOTH the question cards and every validation
   * message: frozen ordinals when available, otherwise one deterministic
   * positional fallback. They can no longer disagree.
   */
  const displayOrdinals = useMemo(
    () => mergeOrdinalMaps(questionOrdinals, buildFallbackOrdinalMap(orderedRows)),
    [questionOrdinals, orderedRows],
  );

  const ordinalFor = useCallback(
    (questionId: number, context: string) => resolveDisplayOrdinal(displayOrdinals, questionId, context),
    [displayOrdinals],
  );

  // --- derived: canonical completeness (same rules as the server) ---
  const completeness = useMemo(
    () =>
      evaluateAssessmentCompleteness({
        questionnaireId: assessment?.questionnaire_id,
        rows,
        responsesById,
        domainOrder: effectiveDomains,
      }),
    [assessment?.questionnaire_id, rows, responsesById, effectiveDomains],
  );

  // --- derived: progress counts ---
  const progressByDomain = useMemo(() => {
    const out: Record<string, { answered: number; total: number }> = {};
    for (const d of effectiveDomains) {
      const b = completeness.byDomain[d];
      out[d] = { answered: b?.answered ?? 0, total: b?.total ?? 0 };
    }
    return out;
  }, [completeness, effectiveDomains]);

  // --- derived: current domain questions ---
  const currentDomainQuestions = useMemo(
    () => rowsByDomain[activeDomain] || [],
    [rowsByDomain, activeDomain]
  );

  const totalQuestions = completeness.totalQuestions;
  const answeredQuestions = completeness.answeredQuestions;
  const overallProgress = completeness.progressPercent;

  /** Build a readable "Q7 – Title" list for a set of question ids. */
  const describeQuestions = useCallback(
    (ids: number[], context: string) =>
      ids.map((id) => {
        const row = rows.find((r) => r.question_id === id);
        const title = row?.title ? ` – ${row.title}` : "";
        return `${questionLabel(ordinalFor(id, context))}${title}`;
      }),
    [rows, ordinalFor],
  );

  /**
   * Next validates the current category and blocks when it is incomplete.
   * The offending questions are highlighted and the category is flagged in
   * the left navigation.
   */
  const handleNextDomain = useCallback(() => {
    const currentIndex = effectiveDomains.indexOf(activeDomain);
    const isLast = currentIndex >= effectiveDomains.length - 1;

    if (!isEffectivelyReadOnly) {
      const blocking = categoryBlockingIds(completeness, activeDomain);
      if (blocking.length > 0) {
        setEvidenceErrors(new Set(blocking));
        setEvidenceErrorMessage(
          `This section is incomplete. Please resolve the following before continuing:\n${describeQuestions(
            blocking,
            "Wizard.next",
          ).join("\n")}`,
        );
        toast({
          variant: "destructive",
          title: "Section incomplete",
          description: `${blocking.length} question(s) in this section still need attention.`,
        });
        return;
      }
      setEvidenceErrors(new Set());
      setEvidenceErrorMessage(null);
    }

    if (!isLast) setActiveDomain(effectiveDomains[currentIndex + 1]);
  }, [activeDomain, effectiveDomains, completeness, describeQuestions, isEffectivelyReadOnly, toast]);


  // --- DB: fetch assessment header + org name ---
  const loadAssessment = useCallback(async (id: string): Promise<AssessmentData> => {
    // Branch 1: Admin — use SECURITY DEFINER RPC (bypasses RLS)
    if (isAdmin) {
      const { data, error } = await supabase.rpc("admin_get_assessment_overview", { p_assessment_id: id });
      if (error) throw error;
      const overview = Array.isArray(data) ? data[0] : data;
      if (!overview) throw new Error("Assessment not found");
      return {
        id: overview.id,
        org_id: overview.portfolio_id,
        questionnaire_id: overview.questionnaire_id,
        status: overview.status,
        submitted_at: overview.submitted_at,
        org_name: overview.portfolio_name ?? "Your Organization",
      };
    }

    // Branch 2: Portfolio Manager / Client Admin — use PM security-definer RPC
    if (effectiveRole === "portfolio_manager" || effectiveRole === "client_admin") {
      const { data: pmData, error: pmErr } = await (supabase.rpc as any)(
        "pm_get_assessment_overview",
        { p_assessment_id: id }
      );
      if (pmErr) throw pmErr;
      const overview = Array.isArray(pmData) ? pmData[0] : pmData;
      if (!overview) throw new Error("Assessment not found");
      return {
        id: overview.id,
        org_id: overview.portfolio_id ?? overview.org_id,
        questionnaire_id: overview.questionnaire_id,
        status: overview.status,
        submitted_at: overview.submitted_at,
        org_name: overview.portfolio_name ?? "Your Organization",
      };
    }

    // Branch 3: Respondent / Reviewer — use SECURITY DEFINER RPC
    const { data: rpcData, error: rpcErr } = await (supabase.rpc as any)(
      "respondent_get_assessment",
      { p_assessment_id: id }
    );
    if (rpcErr) throw rpcErr;
    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
    if (!row) throw new Error("Assessment not found");

    return {
      id: row.id,
      org_id: row.org_id,
      questionnaire_id: row.questionnaire_id,
      status: row.status,
      submitted_at: row.submitted_at,
      org_name: row.org_name ?? "Your Organization",
    };
  }, [isAdmin, effectiveRole]);

  // --- DB: single read for questionnaire items + responses overlay ---
  const initialize = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        setInitError(null);

        // 1) header
        const a = await loadAssessment(id);
        setAssessment(a);

        // Mark assessment as started (respondent only, first open)
        if (!isReadOnlyRole && !isAdmin) {
          try {
            await (supabase.rpc as any)('respondent_mark_started', { p_assessment_id: id });
          } catch (e) {
            console.warn('[Wizard] respondent_mark_started failed (non-fatal):', e);
          }
        }

        // 2) single read (view + responses join + reviewer data for PM)
        const qrows = await fetchAssessmentQuestions({
          assessmentId: id,
          questionnaireId: a.questionnaire_id,
          includeReviewerData: isPM,
          isAdmin,
          isPM,
          portfolioId: a.org_id,
        });

        if (!Array.isArray(qrows) || qrows.length === 0) {
          setRows([]);
          setInitError("No questions found");
          setLoading(false);
          return;
        }

        // 3) build local responses map (scores + comments only)
        const resp = new Map<
          number,
          { score: number | null; comment: string | null; evidence_links?: string[] }
        >();
        for (const r of qrows) {
          if (
            r.score !== null ||
            (r.comment && r.comment.length)
          ) {
            resp.set(r.question_id, {
              score: r.score,
              comment: r.comment ?? null,
              evidence_links: [],
            });
          }
        }

        // 4) Load persisted evidence via secure RPC (single source of truth)
        const { data: evidenceRows, error: evidenceErr } = await (supabase.rpc as any)(
          "get_assessment_evidence",
          { p_assessment_id: id }
        );

        if (evidenceErr) {
          console.warn("[Wizard] evidence load error (non-fatal):", evidenceErr);
        }

        if (Array.isArray(evidenceRows) && evidenceRows.length > 0) {
          // Group evidence URLs by question_id
          const evidenceByQuestion = new Map<number, string[]>();
          for (const ev of evidenceRows) {
            const qid = Number(ev.question_id);
            const url = ev.evidence_type === "file" ? ev.file_url : ev.link_url;
            if (!url) continue;
            const existing = evidenceByQuestion.get(qid) ?? [];
            existing.push(url);
            evidenceByQuestion.set(qid, existing);
          }

          // Merge into responses map
          for (const [qid, urls] of evidenceByQuestion) {
            const cur = resp.get(qid) ?? { score: null, comment: null };
            resp.set(qid, { ...cur, evidence_links: urls });
          }
        }

        setRows(qrows);
        setResponsesById(resp);
      } catch (e: any) {
        console.error("[Wizard] init error:", e);
        setInitError(e?.message ?? "Initialization failed");
      } finally {
        setLoading(false);
      }
    },
    [loadAssessment, isPM]
  );

  useEffect(() => {
    if (!assessmentId) return;
    // Wait for role to be determined before loading (avoids double fetch)
    if (effectiveRole === null) return;
    initialize(assessmentId);
  }, [assessmentId, initialize, effectiveRole]);
  
  // --- fetch reviewer ratings count for PM status summary ---
  useEffect(() => {
    if (!assessmentId) return;

    (async () => {
      if (isAdmin) {
        // Admin path: use security-definer RPC
        const { data } = await (supabase.rpc as any)('admin_list_reviewer_ratings', {
          p_assessment_id: assessmentId,
        });
        const scored = (data ?? []).filter((r: any) => r.reviewer_score !== null);
        setReviewerRatingsCount(scored.length);
      } else if (effectiveRole === 'portfolio_manager' || effectiveRole === 'client_admin') {
        // PM path: use security-definer RPC
        const { data: rrData, error: rrErr } = await (supabase.rpc as any)(
          'pm_get_reviewer_ratings',
          { p_assessment_id: assessmentId }
        );
        if (!rrErr && Array.isArray(rrData)) {
          const scored = rrData.filter((r: any) => r.reviewer_score !== null);
          setReviewerRatingsCount(scored.length);
        } else {
          setReviewerRatingsCount(0);
        }
      } else {
        // Respondent / Reviewer: use SECURITY DEFINER RPC
        const { data: rrData, error: rrErr } = await (supabase.rpc as any)(
          'respondent_get_reviewer_ratings',
          { p_assessment_id: assessmentId }
        );
        if (!rrErr && Array.isArray(rrData)) {
          const scored = rrData.filter((r: any) => r.reviewer_score !== null);
          setReviewerRatingsCount(scored.length);
        } else {
          setReviewerRatingsCount(0);
        }
      }
    })();
  }, [assessmentId, isAdmin, effectiveRole]);

  // --- writes: score/comment ---
  const handleResponseUpdate = useCallback(
    async (
      questionId: number,
      score: number | null,
      comment: string | null,
      evidenceLinks?: string[]
    ) => {
      // Guard: read-only (PM, client_admin, or locked respondent)
      if (isEffectivelyReadOnly) {
        console.warn("[Wizard] handleResponseUpdate blocked (read-only)");
        return;
      }
      if (!assessmentId) return;

      // Check if this is a self-assessment - don't include evidence
      const isSelfAssessment = isSelfAssessmentOnly(assessment?.questionnaire_id);

      const payload = {
        assessment_id: assessmentId,
        question_id: questionId,
        score,
        comment,
        // Only include evidence_links for non-self assessments
        evidence_links: isSelfAssessment ? null : (evidenceLinks ?? null),
      };

      let error: Error | null = null;

      // Determine admin status via RPC (reliable, no RLS dependence)
      const { data: isAdminRpc, error: isAdminErr } = await (supabase.rpc as any)('current_user_is_admin');
      if (isAdminErr) {
        console.error('[SAVE] current_user_is_admin RPC failed', isAdminErr);
        throw isAdminErr;
      }

      // DEBUG: Log which path is being used
      console.log('[SAVE] isAdminRpc=', isAdminRpc, 'effectiveRole=', effectiveRole);

      // Admin path: use RPC to bypass RLS
      if (isAdminRpc) {
        console.log('[SAVE] Using admin_upsert_responses RPC');
        const { error: rpcError } = await (supabase.rpc as any)('admin_upsert_responses', {
          p_rows: [payload]
        });
        error = rpcError;
      } else {
        console.log('[SAVE] Using respondent_upsert_response RPC');
        // Respondent path: use SECURITY DEFINER RPC
        const { error: rpcError } = await (supabase.rpc as any)('respondent_upsert_response', {
          p_assessment_id: assessmentId,
          p_question_id: questionId,
          p_score: score,
          p_comment: comment,
          p_evidence_links: null,
        });
        error = rpcError;
      }

      if (error) {
        console.error("[Save] error", error, payload);
        toast({
          variant: "destructive",
          title: "Save failed",
          description: "Please try again.",
        });
        return;
      }

      setResponsesById((prev) => {
        const next = new Map(prev);
        next.set(questionId, {
          score,
          comment,
          evidence_links: evidenceLinks ?? [],
        });
        return next;
      });

      toast({ title: "Saved", description: "Response saved successfully." });
    },
    [assessmentId, assessment, toast, isPM, isAdmin, effectiveRole]
  );

  // --- writes: evidence only (uses respondent_add_evidence RPC per item) ---
  const handleEvidenceUpdate = useCallback(
    async (questionId: number, links: string[]) => {
      // Guard: read-only
      if (isEffectivelyReadOnly) {
        console.warn("[Wizard] handleEvidenceUpdate blocked (read-only)");
        return;
      }
      if (!assessmentId) return;

      // Diff: detect both ADDED and REMOVED items
      const current = responsesById.get(questionId)?.evidence_links ?? [];
      const newItems = (links ?? []).filter((l) => !current.includes(l));
      const removedItems = current.filter((l) => !(links ?? []).includes(l));

      // Persist removals to canonical assessment_evidence via RPC
      if (removedItems.length > 0) {
        for (const item of removedItems) {
          try {
            const { error: rmErr } = await (supabase.rpc as any)('respondent_remove_evidence', {
              p_assessment_id: assessmentId,
              p_question_id: questionId,
              p_url: item,
            });
            if (rmErr) {
              console.error('[EvidenceUpdate] respondent_remove_evidence error', rmErr);
              toast({
                variant: 'destructive',
                title: 'Could not remove evidence',
                description: rmErr.message ?? 'Please try again.',
              });
              return; // abort UI update so it stays in sync with DB
            }
          } catch (e) {
            console.error('[EvidenceUpdate] respondent_remove_evidence exception', e);
            toast({
              variant: 'destructive',
              title: 'Could not remove evidence',
              description: 'Please try again.',
            });
            return;
          }
        }
      }

      if (newItems.length === 0) {
        // Only removals — update local state to reflect the persisted deletion
        setResponsesById((prev) => {
          const next = new Map(prev);
          const cur = next.get(questionId) ?? { score: null, comment: null };
          next.set(questionId, { ...cur, evidence_links: links });
          return next;
        });
        return;
      }

      // Persist respondent-entered links via respondent_add_evidence.
      // Files uploaded through the secure `upload-evidence` Edge Function are
      // already written to assessment_evidence and are skipped here.
      //
      // Regression fix: links were previously classified as "files" by loose
      // substring matching (e.g. any URL containing "/evidence/", or any value
      // without an http scheme). Those links were silently dropped — they
      // appeared in the UI, vanished on reload, and the server correctly
      // reported the evidence as missing at submission time.
      const persistedItems: string[] = [];
      for (const item of newItems) {
        if (isManagedEvidenceReference(item)) {
          persistedItems.push(item);
          continue;
        }

        const linkUrl = normalizeEvidenceLink(item);
        if (!linkUrl) {
          toast({
            variant: "destructive",
            title: "Invalid evidence link",
            description: `"${item}" is not a valid link. Paste the full URL to the document.`,
          });
          return;
        }

        console.log('[EvidenceUpdate] respondent_add_evidence', { questionId, evidenceType: 'link', linkUrl });

        const { error } = await (supabase.rpc as any)('respondent_add_evidence', {
          p_assessment_id: assessmentId,
          p_question_id: questionId,
          p_evidence_type: 'link',
          p_file_url: null,
          p_link_url: linkUrl,
        });

        if (error) {
          console.error("[EvidenceUpdate] respondent_add_evidence error", error);
          toast({
            variant: "destructive",
            title: "Evidence could not be saved",
            description: error.message ?? "Please try again.",
          });
          return; // keep UI in sync with the DB — do not show unsaved evidence
        }

        persistedItems.push(linkUrl);
      }

      // Reflect the normalized, actually-persisted values in local state.
      const persistedLinks = [
        ...(links ?? []).filter((l) => !newItems.includes(l)),
        ...persistedItems,
      ];


      // 🔹 Kick off AI processing only for telecom_ai_v1
      if (assessment?.questionnaire_id === "telecom_ai_v1") {
        setAiStatus("AI analysis in progress…");

        try {
          await supabase.functions.invoke("ai_process_evidence", {
            body: {
              assessmentId,
              questionId,
            },
          });
          setAiStatus("AI review completed.");
        } catch (e) {
          console.warn("[AI] evidence processing failed", e);
          setAiStatus("AI analysis failed.");
        }
      }

      setResponsesById((prev) => {
        const next = new Map(prev);
        const cur = next.get(questionId) ?? { score: null, comment: null };
        next.set(questionId, { ...cur, evidence_links: persistedLinks });
        return next;
      });

      toast({ title: "Evidence updated", description: "Links attached successfully." });
    },
    [assessmentId, assessment, toast, isPM, responsesById]
  );

  // --- submit assessment ---
  const handleSubmitAssessment = useCallback(async () => {
    // Guard: read-only
    if (isEffectivelyReadOnly) {
      console.warn("[Wizard] handleSubmitAssessment blocked (read-only)");
      return;
    }
    if (!assessment?.id) return;

    const isHrv = isHrvQuestionnaire(assessment.questionnaire_id);

    // --- UI-side validation (identical rules to respondent_submit_assessment) ---
    const { unanswered, missingEvidence, policy } = completeness;

    if (policy.requireAllAnswered && unanswered.length > 0) {
      const questionDetails = describeQuestions(unanswered, "Wizard.unanswered");

      setEvidenceErrorMessage(
        `Please answer all questions before submitting the assessment.\nUnanswered:\n${questionDetails.join("\n")}`
      );
      setEvidenceErrors(new Set(unanswered));

      // Navigate to domain of first unanswered question
      const firstUnanswered = rows.find(r => r.question_id === unanswered[0]);
      if (firstUnanswered && effectiveDomains.includes(firstUnanswered.domain)) {
        setActiveDomain(firstUnanswered.domain);
      }

      toast({
        variant: "destructive",
        title: "Unanswered questions",
        description: `Please answer all ${unanswered.length} remaining question(s) before submitting.`,
      });
      return;
    }

    // HRV: validate at least one vendor selected
    if (isHrv && hrvVendorCount < 1) {
      toast({
        variant: "destructive",
        title: "Vendor selection required",
        description: "Please select at least one Critical Technology Vendor before submitting the assessment.",
      });
      // Scroll to vendor section by navigating to last domain
      setActiveDomain(effectiveDomains[effectiveDomains.length - 1]);
      setTimeout(() => {
        document.querySelector('[data-vendor-section]')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return;
    }

    if (missingEvidence.length > 0) {
      setEvidenceErrors(new Set(missingEvidence));

      const questionDetails = describeQuestions(missingEvidence, "Wizard.evidence");

      const errorMsg = policy.commentSatisfiesEvidence
        ? `The following questions require evidence, a link, or comments based on your selected response:\n${questionDetails.join("\n")}`
        : `Submission blocked. Evidence is required for: ${questionDetails.join(", ")}.`;

      setEvidenceErrorMessage(errorMsg);

      // Navigate to the domain of the first missing question
      const firstMissing = rows.find(r => r.question_id === missingEvidence[0]);
      if (firstMissing && effectiveDomains.includes(firstMissing.domain)) {
        setActiveDomain(firstMissing.domain);
      }

      toast({
        variant: "destructive",
        title: policy.commentSatisfiesEvidence ? "Evidence or comments required" : "Evidence required",
        description: policy.commentSatisfiesEvidence
          ? `Please provide evidence, a link, or comments for ${missingEvidence.length} question(s) before submitting.`
          : `Please provide evidence for ${missingEvidence.length} question(s) before submitting.`,

      });
      return;
    }
    
    // Clear any previous validation errors
    setEvidenceErrors(new Set());
    setEvidenceErrorMessage(null);

    try {
      const { data, error } = await (supabase.rpc as any)('respondent_submit_assessment', {
        p_assessment_id: assessment.id,
      });

      if (error) throw error;
      
      // Handle structured jsonb response from updated RPC
      const result = typeof data === 'string' ? JSON.parse(data) : data;
      
      if (result && result.success === false) {
        // RPC-level validation failure
        if (
          (result.error === 'EVIDENCE_REQUIRED' || result.error === 'EVIDENCE_OR_COMMENT_REQUIRED') &&
          Array.isArray(result.missing_question_ids)
        ) {
          const ids: number[] = result.missing_question_ids.map(Number);
          setEvidenceErrors(new Set(ids));
          const questionDetails = describeQuestions(ids, 'Wizard.evidence2');
          setEvidenceErrorMessage(
            `Submission blocked: ${result.message}\n${questionDetails.join("\n")}`
          );
          // Jump to the first offending question's category so it is visible.
          const firstMissing = rows.find(r => r.question_id === ids[0]);
          if (firstMissing && effectiveDomains.includes(firstMissing.domain)) {
            setActiveDomain(firstMissing.domain);
          }
          toast({
            variant: "destructive",
            title: result.error === 'EVIDENCE_OR_COMMENT_REQUIRED' ? "Evidence or comments required" : "Evidence required",
            description: result.message,
          });
          return;
        }
        if (result.error === 'ALL_QUESTIONS_REQUIRED') {
          setEvidenceErrors(new Set(completeness.unanswered));
          setEvidenceErrorMessage(`Submission blocked: ${result.message}`);
          toast({ variant: "destructive", title: "Unanswered questions", description: result.message });
          return;
        }
        throw new Error(result.message || 'Submission failed');
      }


      toast({
        title: "Assessment submitted!",
        description: "Your responses have been recorded.",
      });

      // Role-aware redirect after submit
      if (effectiveRole === "portfolio_manager") {
        navigate("/pm", { replace: true });
      } else if (effectiveRole === "client_admin") {
        navigate("/client/sub-orgs", { replace: true });
      } else if (effectiveRole === "reviewer" || effectiveRole === "master_reviewer") {
        navigate("/reviewer", { replace: true });
      } else {
        navigate("/dashboard", { replace: true, state: { refresh: true } });
      }
    } catch (err: any) {
      console.error("[Wizard] submit error", err);
      toast({
        variant: "destructive",
        title: "Submit failed",
        description: err?.message ?? "Please try again.",
      });
    }
  }, [assessment, navigate, toast, isPM, effectiveRole, rows, hrvVendorCount, effectiveDomains, completeness, describeQuestions, isEffectivelyReadOnly]);
  
  // --- back navigation based on role ---
  const handleBackClick = useCallback(() => {
    // Admin coming from Admin Console: back to Assessment Overview with state preserved
    if (isAdmin && assessmentId) {
      navigate(`/assessment/${assessmentId}`, { state: { fromAdminConsole: cameFromAdminConsole } });
      return;
    }
    // Tier 1 PM acting as respondent: back to PM dashboard
    if (isPMActingAsRespondent) {
      navigate("/pm");
      return;
    }
    // PM accessing read-only questionnaire: back to overview
    if (effectiveRole === "portfolio_manager" && assessmentId) {
      navigate(`/assessments/${assessmentId}/overview`);
    } else if (effectiveRole === "client_admin") {
      navigate("/client/sub-orgs");
    } else {
      navigate("/dashboard");
    }
  }, [effectiveRole, navigate, assessmentId, isAdmin, cameFromAdminConsole, isPMActingAsRespondent]);

  // --- guards ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>Loading questions…</div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full p-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{initError}</AlertDescription>
          </Alert>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return <div className="p-6">Assessment not found.</div>;
  }

  // --- render ---
  return (
    <AppLayout title={`Assessment — ${assessment.org_name}`} hideBrandingLogo>
      <div className="min-h-screen bg-background">
        {/* Read-only banner for Portfolio Managers and Client Admins */}
        {isPM && (
          <div className="bg-blue-50 dark:bg-blue-950/50 border-b border-blue-200 dark:border-blue-800 px-6 py-2">
            <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200">
              <Eye className="h-4 w-4" />
              <span className="font-medium">Read-only view.</span>
              <span className="text-blue-600 dark:text-blue-300">
                {isClientAdmin 
                  ? "Client Admins cannot modify assessments." 
                  : "Portfolio Managers cannot modify assessments."}
              </span>
            </div>
            {/* DEBUG: role info - remove after verification */}
            <div className="text-xs text-muted-foreground mt-1">
              DEBUG: effectiveRole={String(effectiveRole)} | isClientAdmin={String(isClientAdmin)} | isReadOnlyRole={String(isReadOnlyRole)}
            </div>
          </div>
        )}

        {/* Respondent locked banner */}
        {isRespondentLocked && (
          <div className={`border-b px-6 py-3 ${
            assessment.status === "Closed" 
              ? "bg-muted/80 border-border" 
              : isSelfAssessmentOnly(assessment.questionnaire_id)
                ? "bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800"
                : "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800"
          }`}>
            <div className="flex items-center gap-2 text-sm">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">
                {assessment.status === "Closed"
                  ? "Assessment closed. Contact Portfolio Manager to reopen."
                  : isSelfAssessmentOnly(assessment.questionnaire_id)
                    ? "This assessment has been finalized and cannot be edited. You are viewing submitted responses (read-only)."
                    : "Assessment submitted. Awaiting review."}
              </span>
            </div>
          </div>
        )}

        {/* Pre-submission info banner for self-assessments */}
        {!isRespondentLocked && !isPM && !isAdmin && isSelfAssessmentOnly(assessment?.questionnaire_id) && (
          <div className="border-b px-6 py-2 bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              This assessment must be completed in full before submission. Results will be generated after final submission.
            </p>
          </div>
        )}

        {/* Evidence validation error banner */}
        {evidenceErrorMessage && (
          <div className="bg-destructive/10 border-b border-destructive/30 px-6 py-3">
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="font-medium">{evidenceErrorMessage}</span>
            </div>
          </div>
        )}
        
        {/* sticky header */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                {getQuestionnaireDisplayTitle(assessment.questionnaire_id)}
              </h1>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground/80">{assessment.org_name}</span>
                {displayName && (
                  <>
                    <span className="mx-2 text-border">|</span>
                    <span className="text-muted-foreground">{displayName}</span>
                  </>
                )}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">
                Progress: {answeredQuestions} / {totalQuestions} ({overallProgress}%)
              </div>
              <Button variant="outline" size="sm" onClick={handleBackClick}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                {isPMActingAsRespondent ? "Back to Dashboard" : isPM ? "Back to Overview" : "Dashboard"}
              </Button>
            </div>
          </div>
          
          {/* PM Status Summary */}
          {isPM && (
            <Card className="mt-4 p-4 bg-muted/50">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Status</div>
                  <div className={`font-medium ${assessment.status === "Submitted" ? "text-green-600" : "text-yellow-600"}`}>
                    {assessment.status}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Respondent Progress</div>
                  <div className="font-medium">{answeredQuestions} / {totalQuestions} answered</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Reviewer Progress</div>
                  <div className="font-medium">
                    {reviewerRatingsCount > 0 
                      ? `${reviewerRatingsCount} / ${totalQuestions} scored`
                      : "Not started"
                    }
                  </div>
                </div>
              </div>
            </Card>
          )}
          
          {/* Assignees Panel for PM/client_admin/admin */}
          {canManageAssignees && assessment.org_id && (
            <div className="mt-4">
              <AssessmentAssigneesPanel
                assessmentId={assessment.id}
                portfolioId={assessment.org_id}
              />
            </div>
          )}

          <div className="mt-3">
            <div className="h-2 w-full rounded-full bg-slate-200">
              <div
                className="h-2 rounded-full"
                style={{ 
                  width: `${overallProgress}%`, 
                  backgroundColor: domainTheme[activeDomain as DomainCode]?.solid ?? "#0f172a",
                  transition: "width 180ms ease, background-color 180ms ease"
                }}
              />
            </div>
          </div>

          {assessment.questionnaire_id === "telecom_ai_v1" && aiStatus && (
            <div className="mt-2 text-xs text-muted-foreground">
              {aiStatus}
            </div>
          )}
        </div>

        {/* content */}
        <div className="flex">
          {/* left: domain nav */}
          <aside className="w-72 shrink-0 border-r p-4 space-y-2">
            {effectiveDomains.map((code) => {
              const prog = progressByDomain[code] ?? { answered: 0, total: 0 };
              const pct = prog.total ? Math.round((prog.answered / prog.total) * 100) : 0;
              const isActive = activeDomain === code;
              const domainState = completeness.byDomain[code];
              const blockedIds = domainState
                ? [...domainState.unanswered, ...domainState.missingEvidence]
                : [];
              // Only flag a category once validation has actually reported issues.
              const isFlagged =
                evidenceErrors.size > 0 && blockedIds.some((id) => evidenceErrors.has(id));
              const theme = domainTheme[code as DomainCode] ?? { pill: "bg-slate-100 text-slate-700 ring-slate-300", solid: "#475569" };
              return (
                <button
                  key={code}
                  onClick={() => setActiveDomain(code)}
                  className={`w-full text-left rounded-lg border p-3 transition ${
                    isActive
                      ? "border-primary ring-2 ring-primary"
                      : isFlagged
                        ? "border-destructive ring-1 ring-destructive bg-destructive/5"
                        : "hover:bg-muted"
                  }`}
                >

                  <div className="flex items-center gap-2">
                    {isTriageQuestionnaire ? (
                      <span className="font-medium">{effectiveFunctionNames[code] ?? code}</span>
                    ) : NIST_CSF_DOMAIN_DESCRIPTIONS[code] ? (
                      <TooltipProvider delayDuration={180}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-2 cursor-help">
                              <span 
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${theme.pill} ${isActive ? "ring-2" : ""}`}
                              >
                                {code}
                              </span>
                              <span className="font-medium">{effectiveFunctionNames[code] ?? code}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right" align="start" className="max-w-xs">
                            <p className="font-semibold">{NIST_CSF_DOMAIN_DESCRIPTIONS[code].title}</p>
                            <p className="mt-1 text-muted-foreground">
                              {NIST_CSF_DOMAIN_DESCRIPTIONS[code].description}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <>
                        <span 
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${theme.pill} ${isActive ? "ring-2" : ""}`}
                        >
                          {code}
                        </span>
                        <span className="font-medium">{effectiveFunctionNames[code] ?? code}</span>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {prog.answered}/{prog.total} • {pct}%
                  </div>
                </button>
              );
            })}

            {/* Sidebar submission widget — always visible while editable, so the
                respondent can always see what is blocking submission. */}
            {!isEffectivelyReadOnly && (() => {
              const hrvBlocked =
                isHrvQuestionnaire(assessment.questionnaire_id) && hrvVendorCount < 1;
              const reason = hrvBlocked
                ? "Select at least one Critical Technology Vendor."
                : completeness.blockingReason;
              const ready = !hrvBlocked && completeness.canSubmit;
              return (
                <div className="mt-4 pt-4 border-t space-y-3">
                  <p
                    className={`text-xs font-semibold flex items-center gap-1.5 ${
                      ready ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    <Send className="h-3.5 w-3.5" />
                    {ready ? "Ready to Submit" : "Submission"}
                  </p>
                  {!ready && reason && (
                    <p className="text-xs text-destructive leading-snug">{reason}</p>
                  )}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <Checkbox
                      checked={confirmChecked}
                      onCheckedChange={(checked) => setConfirmChecked(checked === true)}
                      disabled={!ready}
                      className="mt-0.5"
                    />
                    <span className="text-xs text-muted-foreground leading-snug">
                      I confirm that all responses are accurate and complete.
                    </span>
                  </label>
                  <Button
                    onClick={handleSubmitAssessment}
                    disabled={!ready || !confirmChecked}
                    className="w-full"
                    size="sm"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Submit Final Assessment
                  </Button>
                </div>
              );
            })()}

          </aside>

          {/* right: content */}
          <main className="flex-1 overflow-hidden">
            <div className="h-[calc(100vh-6rem)] overflow-y-auto">
              <div className="p-6 max-w-4xl mx-auto">
                <header className="mb-6">
                  <h2 className="text-2xl font-bold">
                    {isTriageQuestionnaire
                      ? (effectiveFunctionNames[activeDomain] ?? activeDomain)
                      : `${activeDomain} — ${effectiveFunctionNames[activeDomain] ?? activeDomain}`}
                  </h2>
                  <div className="text-sm text-muted-foreground">
                    {(progressByDomain[activeDomain] ?? { answered: 0, total: 0 }).answered} of {(progressByDomain[activeDomain] ?? { answered: 0, total: 0 }).total} answered
                  </div>
                  {isTriageQuestionnaire && TRIAGE_SECTION_HELP[activeDomain] && (
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {TRIAGE_SECTION_HELP[activeDomain]}
                    </p>
                  )}
                  {isTriageQuestionnaire && (
                    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <p className="text-xs font-medium text-slate-700">
                        Select the option that best reflects the current state.
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        Consider both internal systems and third-party dependencies when answering.
                      </p>
                    </div>
                  )}
                </header>

                {(() => {
                  // Compute global index offset per domain for continuous numbering
                  const globalOffsets: Record<string, number> = {};
                  let runningOffset = 0;
                  for (const domainKey of effectiveDomains) {
                    globalOffsets[domainKey] = runningOffset;
                    runningOffset += (rowsByDomain[domainKey] || []).length;
                  }

                  const viewsByDomain: Record<string, React.ReactNode> = Object.fromEntries(
                    effectiveDomains.map(domainKey => [
                      domainKey,
                      <DomainSection
                        key={domainKey}
                        domain={domainKey}
                        rows={rowsByDomain[domainKey] || []}
                        responsesById={responsesById}
                        onSave={handleResponseUpdate}
                        onEvidenceUpdate={handleEvidenceUpdate}
                        assessmentId={assessment.id}
                        readOnly={isEffectivelyReadOnly}
                        orgName={assessment.org_name}
                        questionnaireId={assessment.questionnaire_id}
                        evidenceErrorIds={evidenceErrors}
                        globalIndexOffset={globalOffsets[domainKey]}
                        questionOrdinals={displayOrdinals}
                        serviceModels={
                          assessment.questionnaire_id === "telecom_v2"
                            ? (portfolioServiceModels ?? [])
                            : []
                        }
                        serviceModelEvidenceGroups={
                          assessment.questionnaire_id === "telecom_v2"
                            ? evidenceExamplesForServiceModels(portfolioServiceModels)
                            : []
                        }
                      />
                    ])
                  );

                  console.log("Dynamic domain keys:", Object.keys(viewsByDomain));

                  return (
                    <SlideSwitch
                      activeKey={activeDomain}
                      views={viewsByDomain}
                      durationMs={300}
                      maxHeightClass="max-h-[78vh]"
                      minHeightClass="min-h-[420px]"
                    />
                  );
                })()}

                {/* Vendor Selection for HRV assessments */}
                {isHrvQuestionnaire(assessment.questionnaire_id) && activeDomain === effectiveDomains[effectiveDomains.length - 1] && (
                  <div data-vendor-section>
                    <VendorSelectionSection
                      assessmentId={assessment.id}
                      readOnly={isEffectivelyReadOnly}
                      onVendorCountChange={setHrvVendorCount}
                    />
                  </div>
                )}

                {/* Submission card — always rendered on the last category while
                    editable, so the Submit control never disappears. */}
                {!isEffectivelyReadOnly &&
                  activeDomain === effectiveDomains[effectiveDomains.length - 1] && (() => {
                    const isHrv = isHrvQuestionnaire(assessment.questionnaire_id);
                    const hrvBlocked = isHrv && hrvVendorCount < 1;
                    const reason = hrvBlocked
                      ? "Select at least one Critical Technology Vendor before submitting."
                      : completeness.blockingReason;
                    const ready = !hrvBlocked && completeness.canSubmit;

                    return (
                      <Card data-submission-card className="mt-6 p-6 border-primary/30 bg-primary/5">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                          <Send className="h-5 w-5 text-primary" />
                          {ready ? "Ready to Submit" : "Submit Assessment"}
                        </h3>
                        <p className={`text-sm mb-4 ${ready ? "text-muted-foreground" : "text-destructive"}`}>
                          {ready
                            ? "All questions have been answered. Please review your responses before submitting."
                            : reason}
                        </p>
                        <label className="flex items-start gap-2 cursor-pointer mb-4">
                          <Checkbox
                            checked={confirmChecked}
                            onCheckedChange={(checked) => setConfirmChecked(checked === true)}
                            disabled={!ready}
                            className="mt-0.5"
                          />
                          <span className="text-sm text-muted-foreground leading-snug">
                            I confirm that all responses are accurate and complete.
                          </span>
                        </label>
                        <Button
                          onClick={handleSubmitAssessment}
                          disabled={!ready || !confirmChecked}
                          size="lg"
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Submit Final Assessment
                        </Button>
                      </Card>
                    );
                  })()}

                {/* nav buttons */}
                <div className="flex justify-between items-center py-6">
                  <Button
                    variant="outline"
                    onClick={handlePrevDomain}
                    disabled={activeDomain === effectiveDomains[0]}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>

                  {activeDomain !== effectiveDomains[effectiveDomains.length - 1] ? (
                    <Button onClick={handleNextDomain}>
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : isEffectivelyReadOnly ? (
                    <Button variant="outline" onClick={handleBackClick}>
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {isPMActingAsRespondent ? "Back to Dashboard" : isPM ? "Back to Overview" : "Back to Dashboard"}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => document.querySelector('[data-submission-card]')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Go to Submit
                    </Button>
                  )}
                </div>

              </div>
            </div>
          </main>
        </div>
      </div>
    </AppLayout>
  );
}
